import React, { MouseEventHandler, useState, useRef } from "react";
import { Constants } from "../../../common/constants";
import {
	GuildData,
	DiscordAuthToken,
	GetGuildDataRequest,
	DashboardResponse,
	EventCreationProps,
	GetGuildMembersResult,
	GetDataFromGuildRequest,
	GetGuildRolesAndChannelsResult,
	SeshVoiceChannelData,
} from "../../../common/sesh_types";
import BeatLoader from "react-spinners/BeatLoader";
import BounceLoader from "react-spinners/BounceLoader";
import FadeIn from "../../../src/vendored/FadeIn";
import { useHistory } from "react-router-dom";
import AsyncSelect from "react-select/async";
import { common_utils } from "../../../common/common_utils";
import Swal, { SweetAlertCustomClass } from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { WithTooltip } from "./forms/improved_components/tooltips/with_tooltip";
import { InfoTooltip } from "./forms/improved_components/tooltips/info_tooltip";
import classNames from "classnames";
import { isEqual } from "lodash";

const MySwal = withReactContent(Swal);

export interface LinkChainData {
	text: string;
	destination: string;
}

export interface RevertDescriptor<T> {
	default: {
		value: T;
		from: "Guild" | "Channel";
	};
	value: T;
	onRevertPressed: () => void;
}

export interface RepeatData {
	repeat_text: string;
	repeat_id: string;
}

interface ModalProps {
	is_active: boolean;
	title?: JSX.Element | string;
	subtitle?: JSX.Element | string;
	content?: JSX.Element | string;
	onClose: MouseEventHandler<any>;
	allow_overflow?: boolean;
}

export interface IGuildDataCache {
	guild_data: GuildData[];
}

export const FADE_IN_ANIMATION_DURATION = 100;

const swal_custom_classes: SweetAlertCustomClass = {
	popup: "container",
	title: "title",
	htmlContainer: "htmlContainer",
	input: "input",
};

const swal_error_classes: SweetAlertCustomClass = {
	...swal_custom_classes,
	icon: "error-icon",
};

const swal_confirm_delete: SweetAlertCustomClass = {
	...swal_custom_classes,
	popup: "delete-container",
	confirmButton: "delete-button",
};

const swal_confirm_repeat_delete: SweetAlertCustomClass = {
	...swal_confirm_delete,
	denyButton: "delete-button",
};

const swal_success: SweetAlertCustomClass = {
	...swal_custom_classes,
};

type OverridingTagType = "Default" | "Active";

export class DashboardUtils {
	static readonly EVENT_DATA_CACHE_KEY = "event_data_cache";
	static readonly MEMBERS_DATA_CACHE_KEY = "members_data_cache";
	static readonly GUILD_DATA_CACHE_KEY = "guild_data_cache";
	static readonly USER_DATA_CACHE_KEY = "user_data_cache";

	static readonly SESSION_CACHE_KEYS = [
		DashboardUtils.EVENT_DATA_CACHE_KEY,
		DashboardUtils.MEMBERS_DATA_CACHE_KEY,
	];

	static GetEventDataCache() {
		const event_cache_stringifed = sessionStorage.getItem(
			DashboardUtils.EVENT_DATA_CACHE_KEY
		);
		let event_cache: { [key: string]: EventCreationProps };
		if (event_cache_stringifed) {
			event_cache = JSON.parse(event_cache_stringifed);
		} else {
			event_cache = {};
		}

		return event_cache;
	}

	static IsPromise<T>(promise: Promise<T> | T): promise is Promise<T> {
		return (promise as Promise<T>).then !== undefined;
	}

	static async FetchGuildMembers(
		discord_auth_token: DiscordAuthToken,
		guild_id: string
	) {
		const request_body: GetDataFromGuildRequest = {
			access_token: discord_auth_token.access_token,
			token_type: discord_auth_token.token_type,
			guild_id,
		};

		return fetch(`/api/get_guild_members`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(request_body),
		}).then((val) =>
			val.json().then((val: GetGuildMembersResult) => {
				if (val.err) {
					DashboardUtils.ShowDefaultErrorAlert(
						"Failed to fetch guild members."
					);
				}
				return val;
			})
		);
	}

	static async ShowDeleteConfirmAlert(
		text: string | JSX.Element | HTMLElement
	) {
		const customClass = swal_confirm_delete;

		const title = "Delete";

		return MySwal.fire({
			title,
			icon: "warning",
			html: text,
			customClass,
			heightAuto: false,
			showCancelButton: true,
			confirmButtonText: "Confirm Delete",
			showClass: {
				popup: undefined,
				backdrop: "swal2-backdrop-show",
				icon: "swal2-icon-show",
			},
		});
	}

	static async ShowDeleteRepeatConfirmAlert(text: string | JSX.Element) {
		const customClass = swal_confirm_repeat_delete;

		const title = "Delete";

		return MySwal.fire({
			title,
			icon: "warning",
			html: text,
			customClass,
			heightAuto: false,
			showCancelButton: false,
			denyButtonText: "Delete This and Future Events",
			showDenyButton: true,
			confirmButtonText: "Delete Only This Event",
			showClass: {
				popup: undefined,
				backdrop: "swal2-backdrop-show",
				icon: "swal2-icon-show",
			},
		});
	}

	static async ShowSuccessMessageAlert(
		title: string | JSX.Element,
		text: string | JSX.Element
	) {
		const customClass = swal_success;

		return MySwal.fire({
			title,
			icon: "success",
			html: text,
			customClass,
			heightAuto: false,
			showCancelButton: false,
			showClass: {
				popup: undefined,
				backdrop: "swal2-backdrop-show",
				icon: "swal2-icon-show",
			},
		});
	}

	static async ShowResetConfirmAlert(text: string | JSX.Element) {
		const customClass = swal_confirm_delete;

		const title = "Reset Calendar Feeds";

		return MySwal.fire({
			title,
			icon: "warning",
			html: text,
			customClass,
			heightAuto: false,
			showCancelButton: true,
			confirmButtonText: "Confirm Reset",
			showClass: {
				popup: undefined,
				backdrop: "swal2-backdrop-show",
				icon: "swal2-icon-show",
			},
		});
	}

	static async ShowPrompt(
		title: string | JSX.Element,
		text: string | JSX.Element,
		initial_value?: string
	) {
		return MySwal.fire({
			title,
			html: text,
			heightAuto: false,
			customClass: swal_custom_classes,
			input: "text",
			inputValue: initial_value ?? "",
			showClass: {
				popup: undefined,
				backdrop: "swal2-backdrop-show",
				icon: "swal2-icon-show",
			},
		});
	}

	static async ShowDefaultWarningAlert(
		text: string | JSX.Element,
		title?: string | JSX.Element
	) {
		console.log(`WARNING: ${text}`);

		return MySwal.fire({
			title: title ?? "Warning",
			icon: "warning",
			html: text,
			heightAuto: false,
			customClass: swal_error_classes,
			showCancelButton: true,
			cancelButtonText: "Cancel",
			confirmButtonText: "Continue",
			showClass: {
				popup: undefined,
				backdrop: "swal2-backdrop-show",
				icon: "swal2-icon-show",
			},
		});
	}

	static async ShowDefaultErrorAlert(
		text: string | JSX.Element,
		title?: string | JSX.Element,
		onClose?: () => void
	) {
		console.log(`ERROR: ${text}`);

		return MySwal.fire({
			title: title ?? "Error",
			icon: "error",
			html: text,
			heightAuto: false,
			customClass: swal_error_classes,
			showClass: {
				popup: undefined,
				backdrop: "swal2-backdrop-show",
				icon: "swal2-icon-show",
			},
			didClose: () => onClose?.(),
		});
	}

	static async FetchGuildRolesAndChannels(
		discord_auth_token: DiscordAuthToken,
		guild_id: string
	) {
		const request_body: GetDataFromGuildRequest = {
			access_token: discord_auth_token.access_token,
			token_type: discord_auth_token.token_type,
			guild_id,
		};

		return fetch(`/api/get_guild_roles_and_channels`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(request_body),
		}).then((val) =>
			val.json().then((val: GetGuildRolesAndChannelsResult) => {
				if (val.err || !val) {
					DashboardUtils.ShowDefaultErrorAlert(
						"Failed to fetch guild roles and channels"
					);
				}
				return val;
			})
		);
	}

	static GetMembersCache() {
		const members_cache_stringified = sessionStorage.getItem(
			DashboardUtils.MEMBERS_DATA_CACHE_KEY
		);
		let members_cache: { [key: string]: GetGuildMembersResult };
		if (members_cache_stringified) {
			members_cache = JSON.parse(members_cache_stringified);
		} else {
			members_cache = {};
		}

		return members_cache;
	}

	static GetParsedValueFromLocalStorage<T>(key: string) {
		const raw_val = localStorage.getItem(key);
		if (raw_val) {
			return JSON.parse(raw_val) as T;
		}
		return undefined;
	}

	static async FetchUsersGuildData(auth_token: DiscordAuthToken) {
		let request_body: GetGuildDataRequest = {
			access_token: auth_token.access_token,
			token_type: auth_token.token_type,
		};

		const raw_result = await fetch(`/api/get_mutual_servers_with_sesh`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(request_body),
		});

		const result: DashboardResponse<GuildData[]> = await raw_result.json();

		if (result.err) {
			console.log(result.err);
			return;
		}

		const result_to_cache: IGuildDataCache = {
			guild_data: result.props,
		};

		localStorage.setItem(
			DashboardUtils.GUILD_DATA_CACHE_KEY,
			JSON.stringify(result_to_cache)
		);

		return result;
	}

	static RenderFullLoadingThrobberPage(props?: { text?: string }) {
		return (
			<FadeIn
				className="absolute left-0 top-0 flex h-screen w-screen flex-col items-center justify-center gap-4"
				transitionDuration={FADE_IN_ANIMATION_DURATION}
			>
				{props?.text && <div>{props?.text}</div>}

				<BounceLoader
					size={"6rem"}
					loading={true}
					color={Constants.EMBED_COLOR}
				/>
			</FadeIn>
		);
	}

	static GetPlaceholderAvatarURL(id: string, size = 32) {
		return `https://cdn.discordapp.com/embed/avatars/${
			parseInt(id) % 6
		}.png?size=${size}`;
	}

	static GetGuildIconURI(
		data: { id: string; icon?: string },
		size: number = 128
	) {
		let uri: string;
		if (data.icon) {
			uri = `https://cdn.discordapp.com/icons/${data.id}/${data.icon}.png?size=${size}`;
		} else {
			uri = DashboardUtils.GetPlaceholderAvatarURL(data.id, size);
		}

		return uri;
	}

	static RenderLinkChain(props: { link_chain_data: LinkChainData[] }) {
		const history = useHistory();
		const link_chain_data = props.link_chain_data;
		if (link_chain_data.length <= 1) {
			return null;
		}

		let columnized_elements: JSX.Element[] = [];

		let index = 0;
		let i = link_chain_data.length;
		for (let data of link_chain_data) {
			const is_last = index === link_chain_data.length - 1;

			const current_element = (
				<p
					onClick={() => {
						if (!is_last) {
							//this.RenderFullLoadingThrobberPage();
							history.push(data.destination);
						}
					}}
				>
					{data.text}
				</p>
			);

			let className = !is_last ? " link-chain-clickable-text" : "";

			columnized_elements.push(
				<span key={index + "link"} className={className}>
					{current_element}
				</span>
			);

			if (!is_last) {
				columnized_elements.push(
					<span key={index + ">"} className="mx-2">
						{">"}
					</span>
				);
			}

			index++;
		}

		return (
			<div className="columns is-mobile is-multiline link-chain-text">
				{columnized_elements}
			</div>
		);
	}

	static GetGuildSelectionLinkChainData(): LinkChainData {
		return {
			text: "Servers",
			destination: "/dashboard/",
		};
	}

	static LimitEventName(name: string) {
		const limit = Constants.MAX_GUILD_NAME_HEADER_LENGTH_WEB_UI;

		let event_name = name;
		if (name.length > limit) {
			event_name = event_name.substring(0, limit - 3) + "...";
		}

		return event_name;
	}

	static LimitName(name: string, limit: number) {
		let name_to_limit = name;
		if (name.length > limit) {
			name_to_limit = name_to_limit.substring(0, limit - 3) + "...";
		}

		return name_to_limit;
	}

	static RemoveElementFromArray<T>(array: T[], element_to_remove: T) {
		const new_array: T[] = [];
		for (let element of array) {
			if (element === element_to_remove) {
				continue;
			}
			new_array.push(element);
		}
		return new_array;
	}

	static LimitGuildName(name: string, is_header: boolean = false) {
		let limit: number;
		if (is_header) {
			limit = Constants.MAX_GUILD_NAME_HEADER_LENGTH_WEB_UI;
		} else {
			limit = Constants.MAX_GUILD_NAME_LENGTH_WEB_UI;
		}

		let guild_name = name;
		if (name.length > limit) {
			guild_name = guild_name.substring(0, limit - 3) + "...";
		}

		return guild_name;
	}

	static RenderGuildOptionSelectLinkChain(guild_data: GuildData) {
		let guild_name = this.LimitGuildName(guild_data.name);

		return {
			text: guild_name,
			destination: common_utils.getGuildOptionsURL(guild_data.id),
		};
	}

	static RenderTimeFinderSelectionLinkChain(
		guild_data: GuildData,
		type: "create" | "view" | "base" | "edit",
		instance_id?: string
	) {
		let text: string;
		switch (type) {
			case "create":
				text = "Create";
				break;
			case "view":
				text = instance_id ? instance_id : "View";
				break;
			case "base":
				text = "Time Finder";
				break;
			case "edit":
				text = "Edit";
				break;
		}

		return {
			text,
			destination: common_utils.getTimeFinderURL({
				server_id: guild_data.id,
				type,
				instance_id,
			}),
		};
	}

	static RenderEventSelectionLinkChain(guild_data: GuildData) {
		return {
			text: "Events",
			destination: common_utils.getEventListURL(guild_data.id),
		};
	}

	static RenderPollSelectionLinkChain(guild_data: GuildData) {
		return {
			text: "Polls",
			destination: common_utils.getPollListURL(guild_data.id),
		};
	}

	static GetManualLink() {
		return window.location.hostname === "localhost"
			? `${window.location.origin}/manual`
			: `https://sesh.fyi/manual`;
	}

	static DeleteButton(props: { onClick: Function; className?: string }) {
		const classes: string[] = ["error-link"];
		if (props.className) {
			classes.push(props.className);
		}

		return (
			<p className={classes.join(" ")} onClick={() => props.onClick()}>
				<i className="far fa-trash-alt"></i>
			</p>
		);
	}

	static RenderPollResultsLinkChain(guild_data: GuildData, poll_id: string) {
		return {
			text: "Results",
			destination: common_utils.getPollResultsURL(guild_data.id, poll_id),
		};
	}

	// static RenderEventsLinkChain(
	// 	guild_data: GuildData,
	// 	auth_token: DiscordAuthToken,
	// 	fragment: URLSearchParams
	// ) {
	// 	return {
	// 		text: "Events",
	// 		onClickFunc: () => {
	// 			this.GoToEventsPage(guild_data, auth_token, fragment);
	// 		},
	// 	};
	// }

	static GetEventIcon() {
		return <i className="fas fa-calendar-check fa-lg title-icon"></i>;
	}

	static GetPollIcon() {
		return <i className="fas fa-poll fa-lg title-icon"></i>;
	}

	static GetSettingsIcon() {
		return <i className="fas fa-cog fa-lg title-icon"></i>;
	}

	static GetSettingsIconTiny() {
		return <i className="fas fa-cog fa-lg title-icon--tiny"></i>;
	}

	static GetListIcon() {
		return <i className="fas fa-list"></i>;
	}

	static GetBackIcon() {
		return <i className="fas fa-chevron-left"></i>;
	}

	static SeshModal(props: ModalProps) {
		let class_name = "modal";
		if (props.is_active) {
			class_name += ` is-active`;
		}

		return (
			<div className={class_name}>
				<div
					className="modal-background"
					onClick={(e) => {
						props.onClose(e);
					}}
				></div>
				<div
					className={classNames("modal-content ", {
						"!overflow-visible": props.allow_overflow,
						"!overflow-y-auto !overflow-x-clip":
							!props.allow_overflow,
					})}
				>
					{props.title && (
						<div className="modal-title has-text-centered">
							{props.title}
						</div>
					)}
					{props.subtitle && (
						<div className="modal-subtitle has-text-centered">
							{props.subtitle}
						</div>
					)}
					{props.content && (
						<div className="modal-body">{props.content}</div>
					)}
				</div>
				<button
					className="modal-close is-large"
					aria-label="close"
					onClick={(e) => {
						e.preventDefault();
						props.onClose(e);
					}}
				></button>
			</div>
		);
	}

	static CreateOptionsButton(props: {
		button_name: string;
		mobile_button_name?: string;
		icon: JSX.Element;
		on_click: (history?) => void;
		needs_history?: boolean;
	}) {
		let history = props.needs_history ? useHistory() : undefined;

		return (
			<button
				className="dashboard-primary-button is-size-4-tablet is-size-5-mobile is-fullwidth has-text-left"
				type="submit"
				onClick={() => props.on_click(history)}
			>
				<span className="options-icon">{props.icon}</span>
				<p className="hidden sm:block">{props.button_name}</p>
				<p className="block sm:hidden">
					{props.mobile_button_name ?? props.button_name}
				</p>
			</button>
		);
	}

	static RenderGuildSettingsLinkChain(guild_id: string): LinkChainData {
		return {
			text: "Settings",
			destination: common_utils.getGuildSettingsURL(guild_id),
		};
	}

	static RenderGuildChannelPickerLinkChain(guild_id: string): LinkChainData {
		return {
			text: "Overrides",
			destination: common_utils.getGuildChannelPickerURL(guild_id),
		};
	}

	static RenderGuildChannelSettingsLinkChain(
		guild_id: string,
		channel_id: string,
		channel_name: string
	): LinkChainData {
		return {
			text: `#${channel_name}`,
			destination: common_utils.getGuildChannelSettingsURL(
				guild_id,
				channel_id
			),
		};
	}

	static RenderEventCreateLinkChain(
		guild_id: string,
		is_edit: boolean
	): LinkChainData {
		return {
			text: `${is_edit ? "Edit" : "Create"}`,
			destination: common_utils.getEventCreateURL(guild_id),
		};
	}

	static CreateFormConfirmButton(props: {
		is_confirming: boolean;
		blocked_by_load: boolean;
		channel_name: string;
		is_edit: boolean;
		is_event: boolean;
		onClick: (event: any) => void;
	}) {
		let channel_name_text = "";
		if (props.channel_name) {
			channel_name_text = ` in #${props.channel_name}`;
		}

		let channel_name: string;
		const type_string = props.is_event ? "Event" : "Poll";

		if (!props.blocked_by_load) {
			if (props.is_edit) {
				channel_name = `Edit ${type_string}${channel_name_text}`;
			} else {
				channel_name = `Create ${type_string}${channel_name_text}`;
			}
		} else {
			channel_name = "Loading Channels...";
		}

		return (
			<div className="flex items-center gap-2">
				<div className="w-full">
					<button
						className="dashboard-primary-button is-size-4-tablet is-size-5-mobile flex flex-col items-center justify-center truncate"
						onClick={props.onClick}
						disabled={props.is_confirming || props.blocked_by_load}
					>
						{props.is_confirming && (
							<div className="beat-loader-offset">
								<BeatLoader
									margin={"0.75em"}
									loading={true}
									color={"#FFFFFF"}
								/>
							</div>
						)}
						{!props.is_confirming && (
							<span className="has-text-weight-bold w-full truncate">
								{channel_name}
							</span>
						)}
					</button>
				</div>
			</div>
		);
	}

	static IsMobileOS() {
		var userAgent = navigator.userAgent || navigator.vendor;

		// Windows Phone must come first because its UA also contains "Android"
		if (/windows phone/i.test(userAgent)) {
			return true;
		}

		if (/android/i.test(userAgent)) {
			return true;
		}

		// iOS detection from: http://stackoverflow.com/a/9039885/177710
		if (/iPad|iPhone|iPod/.test(userAgent)) {
			return true;
		}

		return false;
	}

	static IsAndroid() {
		var userAgent = navigator.userAgent || navigator.vendor;
		return /android/i.test(userAgent);
	}

	static RenderPollCreateLinkChain(
		guild_id: string,
		is_edit: boolean
	): LinkChainData {
		return {
			text: `${!is_edit ? "Create" : "Edit"}`,
			destination: common_utils.getPollCreateURL(guild_id),
		};
	}

	static RenderLoadingThrobber(loading_text?: string, vertical_spaces = 7) {
		const vertical_spacers: JSX.Element[] = [];
		for (let i = 0; i < vertical_spaces; i++) {
			vertical_spacers.push(<div key={i} className="py-4" />);
		}

		return (
			<div>
				{vertical_spacers}
				<div className="is-centered sweet-loading throbber">
					<div>
						<b>{loading_text}</b>
					</div>
					<div>
						<BeatLoader
							size={"1.5rem"}
							loading={true}
							color={Constants.EMBED_COLOR}
						/>
					</div>
				</div>
				{vertical_spacers}
			</div>
		);
	}

	static IsValidSeshVoiceChannel(
		voice_channel: SeshVoiceChannelData,
		is_stage: boolean
	) {
		if (!voice_channel) {
			return false;
		}

		if (is_stage && !voice_channel.is_stage) {
			return false;
		}

		if (!is_stage && voice_channel.is_stage) {
			return false;
		}

		return (
			voice_channel.sesh_missing_perms.length === 0 &&
			voice_channel.user_missing_perms.length === 0
		);
	}

	static CreateHeaderAndInfoButton<T>(
		header: string | JSX.Element,
		info: string | JSX.Element,
		locked: boolean = false,
		is_beta: boolean = false,
		unsaved_changes: boolean = false,
		checkbox?: JSX.Element,
		disabled: boolean = false,
		is_new = false,
		revert_descriptor?: RevertDescriptor<T> | RevertDescriptor<T>[]
	) {
		const disabled_class = disabled ? " input-disabled" : "";

		let overriding_tag_element: JSX.Element;

		const revert_descriptors = (
			Array.isArray(revert_descriptor)
				? revert_descriptor
				: [revert_descriptor]
		).filter((val) => val);
		if (revert_descriptors.length > 0) {
			const revert_descriptors = Array.isArray(revert_descriptor)
				? revert_descriptor
				: [revert_descriptor];

			const is_channel_override = revert_descriptors.some(
				(val) => val.default.from === "Channel"
			);

			const is_overridden = revert_descriptors.some(
				(val) =>
					!common_utils.areObjectsEqual(val.value, val.default.value)
			);

			if (is_overridden) {
				overriding_tag_element = (
					<span
						className={classNames("tag overriding-default-tag", {
							"input-disabled": disabled,
						})}
						onClick={() => {
							if (disabled) {
								return;
							}
							revert_descriptors.forEach((val) =>
								val.onRevertPressed()
							);
						}}
					>
						<span className="hidden sm:block">
							Revert to Default
						</span>
						<span className="sm:hidden">Revert</span>
					</span>
				);
			} else {
				overriding_tag_element = (
					<span className="tag server-default-tag">
						<span className="hidden sm:block">
							{is_channel_override ? "Channel" : "Server"} Default
						</span>
						<span className="sm:hidden">Default</span>
					</span>
				);
			}
		}

		let label: JSX.Element;
		if (locked) {
			label = (
				<label>
					🔒{" "}
					<a
						className={
							"locked-link input-disabled cursor-pointer" +
							disabled_class
						}
						href={Constants.PREMIUM_LINK}
						target="_blank"
					>
						{header}
					</a>
				</label>
			);
		} else if (checkbox) {
			label = (
				<label className={disabled_class}>
					{checkbox} <span>{header}</span>
				</label>
			);
		} else {
			label = <span className={disabled_class}>{header}</span>;
		}

		return (
			<div className="field">
				<span className="accordian-header is-size-5-tablet is-size-6-mobile flex w-full flex-wrap items-center gap-2 p-0 font-bold">
					<div className="max-w-full flex-shrink-0">
						{label}
						{info && (
							<div className="ml-2 inline-flex items-center">
								<InfoTooltip info={info} />
							</div>
						)}
					</div>
					{is_new && <div className="tag new-tag ml-2">{"New"}</div>}
					{is_beta && (
						<div className="tag beta-tag ml-2">{"Beta"}</div>
					)}
					{unsaved_changes && (
						<div className="tag unsaved-changes-tag ml-2">
							Unsaved Changes
						</div>
					)}
					{overriding_tag_element && (
						<div className="ml-auto">{overriding_tag_element}</div>
					)}
				</span>
			</div>
		);
	}

	static RenderLoadingSelect() {
		return (
			<AsyncSelect
				className="input-disabled"
				classNamePrefix={`sesh-dropdown`}
				placeholder={"Loading..."}
				isLoading={true}
				isDisabled={true}
			/>
		);
	}

	static RenderBackButton(props: { label: string; url: string }) {
		const history = useHistory();

		return (
			<div className="columns is-mobile options-margin-items is-size-6-tablet">
				<div className="column is-narrow">
					<div
						className="columns is-mobile is-vcentered is-variable is-1 margin-link"
						onClick={() => history.push(props.url)}
					>
						<div className="column is-narrow">
							{DashboardUtils.GetBackIcon()}
						</div>
						<div className="column is-narrow">{props.label}</div>
					</div>
				</div>
			</div>
		);
	}

	static RenderErrorContent(props: {
		title: string;
		text: string | JSX.Element;
		back_url?: { href: string; label: string };
	}) {
		return (
			<>
				{props.back_url && (
					<DashboardUtils.RenderBackButton
						label={props.back_url.label}
						url={props.back_url.href}
					/>
				)}
				<h1 className="title error-text">
					<i className="fas fa-exclamation-circle fa-lg title-icon"></i>
					{props.title}
				</h1>
				<p className="error-text has-text-weight-bold">{props.text}</p>
			</>
		);
	}

	static RenderErrorPage(props: {
		title: string;
		text: string;
		back_url?: { href: string; label: string };
	}) {
		return (
			<div
				className="columns is-mobile is-centered"
				style={{ transition: "opacity 0.5s", opacity: "1" }}
			>
				<div
					className={
						"column is-11-mobile is-three-quarters-tablet is-three-quarters-desktop is-half-widescreen is-two-fifths-fullhd create-event-container has-text-centered"
					}
				>
					<DashboardUtils.RenderErrorContent {...props} />
				</div>
			</div>
		);
	}

	static RenderDashboardErrorPage(props: { title: string; text: string }) {
		return (
			<DashboardUtils.RenderErrorPage
				{...props}
				back_url={{
					href: "/dashboard/",
					label: "Servers",
				}}
			/>
		);
	}

	static AddonButton(props: AddonButtonProps) {
		let class_name = "button";

		if (props.selected) {
			class_name += "--is-selected";
		}

		return (
			<p className="control sesh-addon">
				<button
					className={class_name}
					onClick={props.onClick}
					disabled={props.disabled}
				>
					{props.icon && (
						<span className="icon is-small">{props.icon}</span>
					)}
					{props.label && <span>{props.label}</span>}
				</button>
			</p>
		);
	}

	static AddOnButtonGroup(props: { addon_button_data: AddonButtonProps[] }) {
		const addons: JSX.Element[] = props.addon_button_data.map(
			(val, index) => <DashboardUtils.AddonButton key={index} {...val} />
		);

		return (
			<div className="page-selection">
				<div className="field has-addons">{addons}</div>
			</div>
		);
	}
}

export interface AddonButtonProps {
	label?: string;
	icon?: JSX.Element;
	onClick: MouseEventHandler<HTMLButtonElement>;
	selected?: boolean;
	disabled?: boolean;
}

export function RandomAvatar(props: {
	size?: number;
	className?: string;
	seed: string;
}) {
	// Define a palette of pleasing colors
	const colorPalette = [
		"#4F46E5", // Indigo
		"#0EA5E9", // Sky blue
		"#10B981", // Emerald
		"#F59E0B", // Amber
		"#EF4444", // Red
		"#8B5CF6", // Violet
		"#EC4899", // Pink
		"#06B6D4", // Cyan
		"#84CC16", // Lime
		"#6366F1", // Indigo/Purple
	];

	// Generate a deterministic index based on the seed string
	const getHashCode = (str: string) => {
		let hash = 0;
		for (let i = 0; i < str.length; i++) {
			hash = (hash << 5) - hash + str.charCodeAt(i);
			hash |= 0; // Convert to 32bit integer
		}
		return Math.abs(hash);
	};

	const colorIndex = getHashCode(props.seed) % colorPalette.length;
	const size = props.size || 32;

	const style = {
		width: `${size}px`,
		height: `${size}px`,
		backgroundColor: colorPalette[colorIndex],
		color: "white",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		fontSize: `${Math.floor(size * 0.6)}px`,
	};

	return (
		<div style={style} className={`rounded-full ${props.className || ""}`}>
			<i className="fas fa-user"></i>
		</div>
	);
}
